/**
 * Point-to-point commute durations from the user's saved home address to a job, for the job
 * page's "From home: 24 min drive · 41 min bike" chip. Client-side only, and only ever run when
 * the profile actually has a home address, so the server render is never delayed by it.
 *
 * Drive / bike / walk come straight from Mapbox Directions with the public token (the same
 * pattern as the map's commute isochrones: the home point goes to Mapbox in that one request and
 * is never sent to our own backend). Transit goes through /api/commute-time, our thin wrapper
 * around Digitransit's point-to-point `plan` (the key must stay server-side); it is only asked
 * for when that route reports itself available.
 */

export type HomeCommuteProfile = 'driving' | 'cycling' | 'walking' | 'transit';

export interface LatLng {
  lat: number;
  lng: number;
}

/** Minutes per profile; a missing key means "not asked for or not routable", never "zero". */
export type HomeCommuteDurations = Partial<Record<HomeCommuteProfile, number>>;

const MAPBOX_DIRECTIONS_URL = 'https://api.mapbox.com/directions/v5/mapbox';

/** Beyond these straight-line distances the profile is pointless ("4 h 10 min walk") and is skipped. */
export const WALKING_MAX_KM = 6;
export const CYCLING_MAX_KM = 25;
export const DRIVING_MAX_KM = 250;
export const TRANSIT_MAX_KM = 150;

const EARTH_RADIUS_KM = 6371;

export function haversineKm(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Which profiles are worth asking for at this distance. Order is the display order. */
export function profilesForDistance(distanceKm: number, transitAvailable: boolean): HomeCommuteProfile[] {
  const profiles: HomeCommuteProfile[] = [];
  if (transitAvailable && distanceKm <= TRANSIT_MAX_KM) profiles.push('transit');
  if (distanceKm <= DRIVING_MAX_KM) profiles.push('driving');
  if (distanceKm <= CYCLING_MAX_KM) profiles.push('cycling');
  if (distanceKm <= WALKING_MAX_KM) profiles.push('walking');
  return profiles;
}

export function buildDirectionsUrl(profile: Exclude<HomeCommuteProfile, 'transit'>, from: LatLng, to: LatLng, accessToken: string): string {
  const coords = `${from.lng.toFixed(6)},${from.lat.toFixed(6)};${to.lng.toFixed(6)},${to.lat.toFixed(6)}`;
  const query = new URLSearchParams({ overview: 'false', alternatives: 'false', steps: 'false', access_token: accessToken });
  return `${MAPBOX_DIRECTIONS_URL}/${profile}/${coords}?${query.toString()}`;
}

/** Fastest route duration in whole minutes from a Mapbox Directions response body, or null when there is none. */
export function extractDirectionsMinutes(body: unknown): number | null {
  const routes = (body as { routes?: Array<{ duration?: unknown }> } | null)?.routes;
  if (!Array.isArray(routes)) return null;
  let best: number | null = null;
  for (const route of routes) {
    const seconds = typeof route?.duration === 'number' ? route.duration : null;
    if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) continue;
    if (best === null || seconds < best) best = seconds;
  }
  return best === null ? null : Math.max(1, Math.round(best / 60));
}

async function fetchMapboxMinutes(profile: Exclude<HomeCommuteProfile, 'transit'>, from: LatLng, to: LatLng, accessToken: string, signal?: AbortSignal): Promise<number | null> {
  try {
    const response = await fetch(buildDirectionsUrl(profile, from, to, accessToken), { signal });
    if (!response.ok) return null;
    return extractDirectionsMinutes(await response.json());
  } catch {
    return null;
  }
}

async function fetchTransitMinutes(from: LatLng, to: LatLng, signal?: AbortSignal): Promise<number | null> {
  try {
    const query = new URLSearchParams({
      fromLat: from.lat.toFixed(6),
      fromLng: from.lng.toFixed(6),
      toLat: to.lat.toFixed(6),
      toLng: to.lng.toFixed(6),
    });
    const response = await fetch(`/api/commute-time?${query.toString()}`, { signal });
    if (!response.ok) return null;
    const body = (await response.json()) as { transitMinutes?: unknown };
    return typeof body.transitMinutes === 'number' && body.transitMinutes > 0 ? Math.round(body.transitMinutes) : null;
  } catch {
    return null;
  }
}

export interface FetchHomeCommuteParams {
  from: LatLng;
  to: LatLng;
  accessToken: string;
  transitAvailable: boolean;
  signal?: AbortSignal;
}

/** All profiles in parallel; unroutable ones are simply left out of the result. */
export async function fetchHomeCommute({ from, to, accessToken, transitAvailable, signal }: FetchHomeCommuteParams): Promise<HomeCommuteDurations> {
  const profiles = profilesForDistance(haversineKm(from, to), transitAvailable);
  const entries = await Promise.all(
    profiles.map(async (profile): Promise<[HomeCommuteProfile, number | null]> => [
      profile,
      profile === 'transit' ? await fetchTransitMinutes(from, to, signal) : await fetchMapboxMinutes(profile, from, to, accessToken, signal),
    ]),
  );
  const result: HomeCommuteDurations = {};
  for (const [profile, minutes] of entries) {
    if (minutes !== null) result[profile] = minutes;
  }
  return result;
}
